import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Upload, TrendingUp, Menu, X, Tag, Wallet, CreditCard, LogOut, PieChart, ChevronDown, Users, CheckSquare, Download, LockOpen } from 'lucide-react';
import { useAuth } from '../features/auth';
import api from '../services/api';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.FC<{ className?: string }>;
}

const baseNavItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/evolution', label: 'Evolução', icon: TrendingUp },
  { path: '/budgets', label: 'Orçamento', icon: Wallet },
  { path: '/installments', label: 'Parcelamentos', icon: CreditCard },
  { path: '/analytics', label: 'Analítico', icon: PieChart },
  { path: '/categories', label: 'Categorias', icon: Tag },
  { path: '/payments', label: 'Pagamentos', icon: CheckSquare },
  { path: '/imports', label: 'Importar', icon: Upload },
];

const isActive = (pathname: string, path: string) =>
  path === '/' ? pathname === '/' : pathname.startsWith(path);

const DesktopNav: React.FC<{ items: NavItem[]; pathname: string }> = ({ items, pathname }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const measure = () => {
      const nav = navRef.current;
      if (!nav) return;
      const els = Array.from(nav.querySelectorAll<HTMLElement>('[data-nav-item]'));
      if (!els.length) { setVisibleCount(items.length); return; }
      els.forEach(el => { el.style.display = ''; });
      const navRight = nav.getBoundingClientRect().right;
      const lastEl = els[els.length - 1];
      if (lastEl.getBoundingClientRect().right <= navRight) { setVisibleCount(items.length); return; }
      let count = 0;
      for (const el of els) {
        if (el.getBoundingClientRect().right <= navRight - 80) count++;
        else break;
      }
      setVisibleCount(Math.min(count, 4));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [items.length]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const overflow = items.slice(visibleCount);

  return (
    <nav className="hidden md:flex items-center space-x-1 absolute left-1/2 -translate-x-1/2 max-w-[60vw]" ref={navRef}>
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <Link key={item.path} to={item.path} data-nav-item style={i >= visibleCount ? { display: 'none' } : undefined}
            className={`flex items-center space-x-1.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              isActive(pathname, item.path) ? 'text-green-700 bg-green-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}>
            <Icon className="h-4 w-4" />
            <span>{item.label}</span>
          </Link>
        );
      })}
      {overflow.length > 0 && (
        <div className="relative" ref={moreRef}>
          <button onClick={() => setMoreOpen(!moreOpen)}
            className={`flex items-center space-x-1 px-2.5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              overflow.some(e => isActive(pathname, e.path)) ? 'text-green-700 bg-green-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}>
            <span>Mais</span>
            <ChevronDown className={`h-3 w-3 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
          </button>
          {moreOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
              {overflow.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMoreOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                      isActive(pathname, item.path) ? 'text-green-700 bg-green-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}>
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const navItems = user?.role === 'admin'
    ? [...baseNavItems, { path: '/admin/users', label: 'Usuários', icon: Users }]
    : baseNavItems;

  // Pages that use full width (more screen space for tables)
  const fullWidth = location.pathname === '/' || location.pathname === '/evolution' || location.pathname === '/categories' || location.pathname === '/budgets' || location.pathname === '/installments' || location.pathname === '/payments' || location.pathname === '/analytics' || location.pathname === '/imports';
  // Full width with same padding as header for alignment
  const mainClass = fullWidth ? 'w-full px-4 sm:px-6 lg:px-32' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-20">
        <div className="w-full px-4 sm:px-6 lg:px-32">
          <div className="flex justify-between items-center h-16 relative">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 shrink-0">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-sm">$</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Financeiro</h1>
                <p className="text-xs text-gray-500 hidden xl:block">Gestão Financeira Pessoal</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <DesktopNav items={navItems} pathname={location.pathname} />

            {/* Mobile hamburger */}
            <div className="flex items-center gap-2">
              {user?.role === 'admin' && (
                <ReleaseImportButton />
              )}
              {user && <UserDropdown user={user} logout={logout} />}
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100">
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-2 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                      isActive(location.pathname, item.path)
                        ? 'text-green-700 bg-green-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}>
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              {user && (
                <button onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 w-full">
                  <LogOut className="h-5 w-5" />
                  <span>Sair ({user.name.split(' ')[0]})</span>
                </button>
              )}
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className={`${mainClass} py-4 sm:py-8 flex-1 overflow-hidden`}>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-sm text-gray-500">
            © 2026 Financeiro — Gestão Financeira Pessoal
          </div>
        </div>
      </footer>
    </div>
  );
};

// === Release Import Button (admin only) ===
const ReleaseImportButton: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    const confirmed = window.confirm('Liberar importação para todos os usuários?');
    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await api.post('/import-status/reset-all');
      const released: number = response.data?.data?.released ?? 0;
      alert(`${released} usuário(s) liberado(s).`);
    } catch {
      alert('Erro ao liberar importação.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      title="Liberar Importação"
      className="btn-outline flex items-center gap-1.5 px-3 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <LockOpen className="h-4 w-4" />
      <span className="hidden lg:inline">Liberar Importação</span>
    </button>
  );
};

// === User Dropdown Menu ===
const UserDropdown: React.FC<{ user: { name: string; picture: string; email?: string }; logout: () => void }> = ({ user, logout }) => {
  const [open, setOpen] = useState(false);
  const [pwaPrompt, setPwaPrompt] = useState<any>(null);

  useEffect(() => {
    const { getPwaInstallPrompt } = require('../index');
    const existing = getPwaInstallPrompt();
    if (existing) setPwaPrompt(existing);
    const handler = (e: Event) => { e.preventDefault(); setPwaPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
        <ChevronDown className="w-3.5 h-3.5 text-gray-500 hidden sm:block" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            <div className="p-3 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
              {user.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
            </div>
            <div className="py-1">
              {pwaPrompt && (
                <button
                  onClick={async () => {
                    pwaPrompt.prompt();
                    await pwaPrompt.userChoice;
                    const { clearPwaInstallPrompt } = require('../index');
                    clearPwaInstallPrompt();
                    setPwaPrompt(null);
                    setOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Download className="w-4 h-4 mr-3" />
                  Instalar App
                </button>
              )}
              <button onClick={() => { logout(); setOpen(false); }}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-3" />
                Sair
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Layout;
